import {
  Badge,
  Box,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { CommitmentTabProps } from "features/commitments/view/tabs/CommitmentTabProps";
import {
  useHypotheticalWaterfallPerformanceFilterDataRetrieveSuspense,
  useHypotheticalWaterfallPerformanceStatsByGranularityRetrieveSuspense,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundFamilyListSuspense } from "gen/endpoints/fund-family/fund-family";
import { HypotheticalDateQuarter } from "gen/model/hypotheticalDateQuarter";
import { compact } from "lodash-es";
import { number } from "mathjs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { FundFamilySelectButton } from "shared/components/input/select/FundFamilySelectButton";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import QuarterSelectModal from "shared/components/modal/QuarterSelectModal";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxCollapse } from "shared/components/table/box/TableBoxCollapse";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { Edit2Icon } from "shared/icons/Edit2Icon";
import { formatPercentage } from "shared/utils/formattingUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { NonNullStringArrayParam } from "shared/utils/QueryParamUtils";
import { useQueryParam } from "use-query-params";

export const HypotheticalPerformanceTabLegacy: React.FC = () => {
  const { investorId, investorEntityId, commitmentId, fundFamilyId } =
    useOutletContext<CommitmentTabProps>();

  const [effectiveDate, setEffectiveDate] = useState<string | undefined>(
    undefined,
  );

  const { data: fundFamilies } = useFundFamilyListSuspense({
    investor_id: investorId,
    investor_entity_id: investorEntityId,
  });
  const { t } = useTranslation(["financialReporting"]);

  const { data: fundFamilyFilterData } =
    useHypotheticalWaterfallPerformanceFilterDataRetrieveSuspense({
      fund_family_ids: fundFamilyId
        ? [fundFamilyId]
        : fundFamilies.map((fundFamily) => String(fundFamily.id)),
      effective_date: effectiveDate ? effectiveDate : undefined,
    });
  const fundFamilyIdToIsEnabled =
    fundFamilyFilterData.fund_family_filter_options?.reduce(
      (accumulator, currentValue) => {
        accumulator[currentValue.id] = !currentValue.is_disabled;
        return accumulator;
      },
      {} as Record<string, boolean>,
    ) || ({} as Record<string, boolean>);
  const selectableFundFamilyIds = fundFamilyId
    ? [fundFamilyId]
    : compact(
        fundFamilies.map((fundFamily) =>
          fundFamilyIdToIsEnabled[fundFamily.id!] ? fundFamily.id : false,
        ),
      );

  const [selectedDateQuarter, setDateQuarter] = useState<
    HypotheticalDateQuarter | undefined
  >(
    fundFamilyFilterData.latest_date_quarter
      ? fundFamilyFilterData.latest_date_quarter
      : undefined,
  );

  const [fundFamilyIds, setFundFamilyIds] = useQueryParam(
    "fundFamilyId",
    NonNullStringArrayParam(selectableFundFamilyIds),
  );

  const { data: hypoData } =
    useHypotheticalWaterfallPerformanceStatsByGranularityRetrieveSuspense({
      investor_id: investorId,
      investor_entity_id: investorEntityId,
      security_ownership_id: commitmentId,
      fund_family_ids: fundFamilyIds,
      effective_date: selectedDateQuarter?.effective_date,
    });

  const singleFundFamily = commitmentId;

  const {
    isOpen: isEditQuarterModalOpen,
    onOpen: openEditQuarterModal,
    onClose: closeEditQuarterModal,
  } = useDisclosure();

  return (
    <PageTabBody>
      <Card variant="table" mb="24px">
        <CardHeader>
          <Text>{t("financialReporting:performanceTab.title")}</Text>
          <Box mr="auto" ml="8px">
            <TableBoxBadges>
              {!singleFundFamily && (
                <Badge
                  colorScheme="primary"
                  variant="button"
                  onClick={() => openEditQuarterModal()}
                >
                  {t("financialReporting:performanceTab.viewingAsOf", {
                    periodValue: selectedDateQuarter?.period_rich_value,
                  })}
                  <Edit2Icon />
                </Badge>
              )}
              {fundFamilyIds.length > 0 && (
                <>
                  {fundFamilyFilterData.fund_family_filter_options?.map(
                    (filterOption) =>
                      fundFamilyIds.some((id) => filterOption.id === id) ? (
                        <Badge key={filterOption.id}>{filterOption.name}</Badge>
                      ) : null,
                  )}
                  {fundFamilyId && (
                    <Badge size="sm">
                      {t("financialReporting:performanceTab.dataAsOf", {
                        periodValue: selectedDateQuarter?.period_rich_value,
                      })}
                    </Badge>
                  )}
                </>
              )}
            </TableBoxBadges>
          </Box>
          {!singleFundFamily && isEditQuarterModalOpen && (
            <QuarterSelectModal
              allDateQuarters={fundFamilyFilterData.all_date_quarters!}
              selectedDateQuarter={selectedDateQuarter!}
              onSelectedDateQuarter={setDateQuarter}
              onCloseComplete={closeEditQuarterModal}
              onChangeSuccess={(date) => setEffectiveDate(date)}
            />
          )}
          {!singleFundFamily &&
            fundFamilyFilterData.fund_family_filter_options && (
              <FundFamilySelectButton
                fundFamilyFilterOptions={
                  fundFamilyFilterData.fund_family_filter_options
                }
                selectedFundFamilyIds={fundFamilyIds}
                onSelectedFundFamilyIdsChange={setFundFamilyIds}
              />
            )}
        </CardHeader>
        <CardBody pb="0">
          <StatGroup>
            <Stat size="sm">
              <StatNumber>{formatMoney(hypoData.stats.total_nav)}</StatNumber>
              <StatLabel>
                {t("financialReporting:performanceTab.stats.nav")}
              </StatLabel>
            </Stat>
            <Stat size="sm">
              <StatNumber>
                {formatPercentage(
                  number(hypoData.stats.total_gross_irr).toFixed(2),
                )}
              </StatNumber>
              <StatLabel>
                {t("financialReporting:performanceTab.stats.grossIRR")}
              </StatLabel>
            </Stat>
            <Stat size="sm">
              <StatNumber>
                {formatPercentage(
                  number(hypoData.stats.total_net_irr).toFixed(2),
                )}
              </StatNumber>
              <StatLabel>
                {t("financialReporting:performanceTab.stats.netIRR")}{" "}
              </StatLabel>
            </Stat>
            <Stat size="sm">
              <StatNumber>
                {formatPercentage(
                  number(hypoData.stats.total_gross_to_net_irr_spread).toFixed(
                    2,
                  ),
                )}
              </StatNumber>
              <StatLabel>
                {t(
                  "financialReporting:performanceTab.stats.grossToNetIRRSpread",
                )}
              </StatLabel>
            </Stat>
          </StatGroup>
          <TableBox isDefaultExpanded={false} isFullWidth={true}>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("financialReporting:performanceTab.allMetrics.title")}
              </TableBoxTitle>
              <DataTableBoxActions />
            </TableBoxHeader>
            <TableBoxCollapse>
              <TableBoxBody mb="0">
                <DataTable
                  data={hypoData.table}
                  emptyStateProps={{
                    title: t(
                      "financialReporting:performanceTab.emptyState.title",
                    ),
                    description: t(
                      "financialReporting:performanceTab.emptyState.description",
                    ),
                  }}
                />
              </TableBoxBody>
            </TableBoxCollapse>
          </TableBox>
        </CardBody>
      </Card>
    </PageTabBody>
  );
};
